import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

const savedLng = (typeof window !== 'undefined' && localStorage.getItem('lang')) || 'en';

const resources = {
  en: {
    common: {
      language: 'Language',
      notifications: 'Notifications',
      notification: 'Notification',
      markAllRead: 'Mark all as read',
      viewAll: 'View All',
      pagination: {
        rowsPerPage: 'Rows:',
        displayedRows: '{{from}}-{{to}} / {{count}}'
      },
      selection: {
        selectedRows: 'Selected: {{count}}'
      },
      actions: {
        reset: 'Reset',
        filter: 'Filter',
        export: 'Export',
        view: 'View',
        delete: 'Delete',
        cancel: 'Cancel',
        close: 'Close'
      },
      fields: {
        search: 'Search'
      },
      aria: {
        editBatchNumber: 'Edit batch number',
        editAdditionalIdentifier: 'Edit additional identifier'
      },
      constants: {
        none: 'NONE',
        na: 'N/A'
      },
      conformity: {
        ok: 'OK',
        ko: 'KO',
        noData: 'NO DATA'
      },
      lineChart: {
        tooltip: {
          wavelength: 'Wavelength',
          absorbance: 'Absorbance',
          productCode: 'Product Code',
          product: 'Product Name',
          batchNumber: 'Batch Number',
          repetitionShort: 'Rep.',
          registrationDateShort: 'Reg. Date'
        }
      },
      advancedFilters: {
        label: 'Advanced Filters'
      },
      advancedFiltersResults: {
        title: 'Advanced Filters',
        addFilterTitle: 'Add filter',
        addFilterAction: 'Add filter',
        selectedFiltersTitle: 'Selected Filters ({{count}})',
        clearFilters: 'Clear filters',
        emptyState: {
          noFiltersApplied: "This view doesn't have any advanced filters applied."
        },
        placeholders: {
          selectColumn: 'Select column',
          selectSupplier: 'Select supplier',
          selectProduct: 'Select product',
          selectConformity: 'Select conformity',
          selectConformityLabel: 'Select conformity:'
        },
        dateRange: {
          from: 'From',
          to: 'To'
        },
        errors: {
          duplicate: 'The filter already exists.'
        },
        operators: {
          lt: 'is less than',
          lte: 'is less than or equal to',
          eq: 'is equal to',
          neq: 'is not equal to',
          gt: 'is greater than',
          gte: 'is greater than or equal to',
          contains: 'contains',
          not_contains: 'does not contain',
          starts_with: 'starts with',
          ends_with: 'ends with',
          is_empty: 'is empty',
          is_not_empty: 'is not empty',
          is_highest_repetition: 'is the highest repetition'
        }
      },
      pages: {
        results: {
          title: 'Results',
          dialogs: {
            selectProductBeforeFilter: 'Select a product before filter'
          }
        },
        products: {
          selectAllProducts: '< SELECT ALL PRODUCTS >'
        },
        supportHelpCenter: {
          title: 'Help Center'
        },
        spectraEquipments: {
          title: 'Nir Equipments',
          searchPlaceholder: 'Search equipment',
          add: 'Add equipment',
          edit: 'Edit',
          delete: 'Delete',
          empty: 'No equipment matches your search.',
          none: 'No equipment found for this company.',
          loadError: 'Could not load equipment.',
          columns: {
            name: 'Equipment',
            companyGroup: 'Company group',
            location: 'Location',
            manufacturer: 'Manufacturer',
            model: 'Model',
            serialNumber: 'Serial no.',
            resolution: 'Wavelength resolution',
            notes: 'Notes'
          },
          form: {
            addTitle: 'Add equipment',
            editTitle: 'Edit equipment',
            close: 'Close',
            cancel: 'Cancel',
            save: 'Save equipment',
            saveChanges: 'Save changes',
            optional: 'Optional',
            required: 'This field is required',
            labels: {
              name: 'Name / identifier',
              companyGroup: 'Company group',
              location: 'Location',
              manufacturer: 'Manufacturer',
              model: 'Model',
              serialNumber: 'Serial number',
              resolution: 'Wavelength resolution',
              notes: 'Notes'
            }
          }
        },
        spectraImport: {
          title: 'Upload spectra',
          continue: 'Continue',
          back: 'Back',
          steps: {
            equipment: 'Equipment',
            file: 'File',
            validation: 'Validation',
            result: 'Result'
          },
          equipment: {
            instruction: 'Select the NIR equipment these spectra belong to.',
            searchPlaceholder: 'Search by name, manufacturer, or model',
            manage: "Don't see your equipment? Manage equipment",
            empty: 'No equipment matches your search.',
            none: 'No equipment found for this company.',
            loadError: 'Could not load equipment.'
          },
          file: {
            uploadingFor: 'Uploading spectra for the equipment',
            dropTitle: 'Drag your file here, or click to browse',
            supportedFormats: 'Supported formats: .csv, .nir, .sp, .spc, .jdx, .json, .opus, .spa',
            removeFile: 'Remove file',
            invalidFormat: 'Unsupported file format. Use csv, .nir, .sp, .spc, .jdx, .json, .opus, .spa.',
            downloadTemplate: 'Download CSV template',
            howToFill: 'How to fill in the CSV',
            howToFillIntro: 'Each row is one spectrum reading. Required columns:',
            howToFillItems: {
              sampleId: 'Sample ID — unique per row',
              product: 'Product Code / Product Name — must exist in the catalog',
              batch: 'Batch Number, Registration Date (DD/MM/YYYY), Repetition (≥1)',
              spectrum: "Wavelength / Absorbance — one column pair per measured point, within the equipment's calibration range"
            },
            validate: 'Validate file'
          },
          validation: {
            fileReadOk: 'File read successfully',
            reading: 'Reading file and checking spectra…',
            readingHint: 'This usually takes a few seconds.',
            found: '{{count}} spectra found',
            valid: '{{count}} valid',
            withErrors: '{{count}} with errors',
            breakdown: '({{blocking}} blocking, {{warnings}} warnings).',
            blockingBanner: 'There are {{count}} errors. Go back and upload a corrected file to continue.',
            loadError: 'Could not validate the file.',
            importAction: 'Import {{count}} spectra',
            columns: {
              row: 'Row',
              sample: 'Sample',
              field: 'Field',
              error: 'Error',
              recommendedAction: 'Recommended action',
              status: 'Status'
            },
            statusBlocking: 'Blocking',
            statusWarning: 'Warning'
          },
          result: {
            title: 'Import completed',
            summary: '{{count}} spectra were imported and associated with',
            viewImported: 'View imported spectra',
            importAnother: 'Import another file'
          }
        },
        spectraViewer: {
          title: 'Spectra Viewer',
          actions: {
            hideFilter: 'Hide Filter',
            showFilter: 'Show Filter'
          },
          chart: {
            zoom: 'Zoom',
            resetZoom: 'Reset zoom',
            saveImage: 'Save as image',
            enableInteractive: 'Enable crosshair and tooltip',
            disableInteractive: 'Disable crosshair and tooltip',
            showLegend: 'Show legend',
            hideLegend: 'Hide legend',
            showChartTitle: 'Show chart title',
            hideChartTitle: 'Hide chart title',
            productNamesEmpty: 'No product name',
            legendLabelsByLot: 'Legend: batch numbers',
            legendLabelsByProduct: 'Legend: product names',
            toggleLegendLabelsToProduct: 'Use product names in legend',
            toggleLegendLabelsToLot: 'Use batch numbers in legend'
          },
          dialogs: {
            selectProductBeforeFilter: 'Select a product before filter',
            wavelengthsNotSame: 'The selected spectra do not have the same wavelengths.',
            tooManyForChart: 'Too many spectra to display in the chart ({{count}}). Maximum allowed is {{max}}.',
            saveBatchFailed: 'Could not save the batch number. {{error}}',
            saveAdditionalIdentifierFailed: 'Could not save the additional identifier. {{error}}',
            saveProductFailed: 'Could not save the product. {{error}}',
            saveSupplierFailed: 'Could not save the supplier. {{error}}',
            confirmDelete: 'Are you sure you want to delete {{count}} selected spectrum(s)? This action cannot be undone.',
            deleting: 'Deleting {{count}} spectrum(s)…',
            deletedOk: 'Deleted {{count}} spectrum(s) successfully.',
            deleteNoneDeleted: 'Could not delete any of the {{count}} spectrum(s).',
            deleteSomeFailed: 'Deleted {{ok}} spectrum(s). Could not delete {{failed}}.',
            notDeletedLine: 'Batch Number: {{batch}}  -  Reg. Date: {{reg}}  -  Repetition: {{rep}}'
          }
        }
      },
      menu: {
        groups: {
          spectra: 'Spectra',
          analytics: 'Analytics',
          support: 'Support'
        },
        items: {
          spectraViewer: 'Spectra Viewer',
          spectraImport: 'Spectra Import',
          equipments: 'Nir Equipments',
          results: 'Results',
          dashboards: 'Dashboards',
          helpCenter: 'Help Center'
        }
      },
      datagrid: {
        headers: {
          productCode: 'Product Code',
          productName: 'Product Name',
          batchNumber: 'Batch Number',
          registrationDate: 'Registration Date',
          repetition: 'Repetition',
          additionalIdentifier: 'Additional Identifier',
          supplierName: 'Supplier Name',
          conformity: 'Conformity'
        }
      },
      supportTicket: {
        tooltip: 'Support',
        title: 'Support Ticket',
        intro: 'Describe the issue you’re experiencing to understand the situation and help you resolve it quickly. You can attach files to help us understand the issue better.',
        placeholder: 'Describe your issue or request…',
        subject: 'Support ticket',
        success: 'Ticket sent successfully.',
        email: {
          greeting: 'Dear {{name}},',
          received: 'We have received your ticket and it has been created successfully.',
          reviewing: 'The Syn Support Team is now reviewing your request to improve and resolve the issues as soon as possible.',
          userInformationTitle: 'User information:',
          ticketDescriptionTitle: 'Ticket description:',
          fields: {
            name: 'Name',
            email: 'Email',
            company: 'Company'
          }
        },
        actions: {
          send: 'Send ticket',
          sending: 'Sending…'
        },
        attach: {
          tooltip: 'Attach files',
          aria: 'attach files'
        },
        aria: {
          open: 'open support ticket',
          close: 'close',
          removeAttachment: 'remove {{fileName}}'
        },
        errors: {
          sendFailed: 'The ticket could not be sent.',
          sendFailedWithStatus: 'Error sending ticket: {{status}} {{statusText}}',
          attachmentsTotalTooLarge: 'The total size of attachments cannot exceed {{maxMB}} MB.',
          attachmentsTotalTooLargeSomeOmitted: 'Some files were omitted: the total size of attachments cannot exceed {{maxMB}} MB.'
        }
      },
      auth: {
        actions: {
          logout: 'Logout'
        },
        session: {
          warningTitle: 'Session expiring soon',
          warningMessage: 'For security, your session will close in {{time}}.\nLog in again to continue working.',
          logoutNow: 'Logout now'
        },
        login: {
          title: 'Welcome Syn',
          titlePrefix: 'Welcome',
          titleSuffix: 'Syn',
          fields: {
            email: 'Email Address',
            password: 'Password'
          },
          placeholders: {
            email: 'Enter email address',
            password: 'Enter password'
          },
          rememberCredentials: 'Remember credentials',
          forgotPassword: 'Forgot Password?',
          actions: {
            login: 'Login',
            loggingIn: 'Logging in ...'
          },
          validation: {
            emailInvalid: 'Must be a valid email',
            emailRequired: 'Email is required',
            passwordRequired: 'Password is required',
            passwordNoEdgeSpaces: 'Password cannot start or end with spaces',
            passwordMax: 'Password must be less than 20 characters'
          },
          errors: {
            loginFailed: 'Login failed',
            connectionFailed: 'Connection error. Try again.'
          }
        },
        forgotPassword: {
          title: 'Forgot Password',
          intro: "Enter your email address and we’ll send you instructions to reset your password. Please check your inbox.",
          fields: {
            email: 'Email Address'
          },
          placeholders: {
            email: 'Enter email address'
          },
          actions: {
            send: 'Send Reset Email',
            sending: 'Sending ...',
            backToLogin: 'Back login'
          },
          validation: {
            emailInvalid: 'Must be a valid email',
            emailRequired: 'Email is required'
          },
          errors: {
            sendFailed: 'Email could not be sent'
          }
        },
        resetPassword: {
          title: 'Reset Password',
          intro: 'Enter your new password and confirm',
          fields: {
            newPassword: 'New password',
            confirmPassword: 'Confirm password'
          },
          placeholders: {
            newPassword: 'Enter new password',
            confirmPassword: 'Confirm new password'
          },
          actions: {
            save: 'Save new password',
            saving: 'Saving ...'
          },
          validation: {
            passwordRequired: 'Password is required',
            passwordMin: 'Minimum 8 characters',
            passwordMax: 'Maximum 64 characters',
            passwordLettersAndNumbers: 'Must contain letters and numbers',
            passwordNoEdgeSpaces: 'Password cannot start or end with spaces',
            confirmPasswordRequired: 'Confirm your password',
            passwordsMustMatch: 'Passwords must match'
          },
          errors: {
            resetFailed: 'The link has expired and the password could not be reset. Please request a new link.',
            resetFailedWithStatus: 'Could not reset password: {{status}} {{statusText}}',
            missingToken: 'Missing token. Request a new reset email.',
            invalidToken: 'Invalid or malformed token.'
          },
          success: {
            resetOk: 'Password reset successfully. Please log in.'
          }
        }
      }
    }
  },
  es: {
    common: {
      language: 'Idioma',
      notifications: 'Notificaciones',
      notification: 'Notificación',
      markAllRead: 'Marcar todo como leído',
      viewAll: 'Ver todo',
      pagination: {
        rowsPerPage: 'Filas:',
        displayedRows: '{{from}}-{{to}} / {{count}}'
      },
      selection: {
        selectedRows: 'Seleccion: {{count}}'
      },
      actions: {
        reset: 'Reiniciar',
        filter: 'Filtrar',
        export: 'Exportar',
        view: 'Ver',
        delete: 'Eliminar',
        cancel: 'Cancelar',
        close: 'Cerrar'
      },
      fields: {
        search: 'Buscar'
      },
      aria: {
        editBatchNumber: 'Editar número de lote',
        editAdditionalIdentifier: 'Editar identificador adicional'
      },
      constants: {
        none: 'NONE',
        na: 'N/D'
      },
      conformity: {
        ok: 'OK',
        ko: 'KO',
        noData: 'SIN DATOS'
      },
      lineChart: {
        tooltip: {
          wavelength: 'Longitud Onda',
          absorbance: 'Absorbancia',
          productCode: 'Código Producto',
          product: 'Nombre Producto',
          batchNumber: 'Número Lote',
          repetitionShort: 'Rep.',
          registrationDateShort: 'F. Registro'
        }
      },
      advancedFilters: {
        label: 'Filtros avanzados'
      },
      advancedFiltersResults: {
        title: 'Filtros avanzados',
        addFilterTitle: 'Añadir filtro',
        addFilterAction: 'Añadir filtro',
        selectedFiltersTitle: 'Filtros seleccionados ({{count}})',
        clearFilters: 'Limpiar filtros',
        emptyState: {
          noFiltersApplied: 'Esta vista no tiene filtros avanzados aplicados.'
        },
        placeholders: {
          selectColumn: 'Selecciona columna',
          selectSupplier: 'Selecciona proveedor',
          selectProduct: 'Selecciona producto',
          selectConformity: 'Selecciona conformidad',
          selectConformityLabel: 'Selecciona conformidad:'
        },
        dateRange: {
          from: 'Desde',
          to: 'Hasta'
        },
        errors: {
          duplicate: 'Ese filtro ya existe.'
        },
        operators: {
          lt: 'es menor que',
          lte: 'es menor o igual que',
          eq: 'es igual a',
          neq: 'no es igual a',
          gt: 'es mayor que',
          gte: 'es mayor o igual que',
          contains: 'contiene',
          not_contains: 'no contiene',
          starts_with: 'empieza por',
          ends_with: 'termina en',
          is_empty: 'está vacío',
          is_not_empty: 'no está vacío',
          is_highest_repetition: 'es la repetición más alta'
        }
      },
      pages: {
        results: {
          title: 'Resultados',
          dialogs: {
            selectProductBeforeFilter: 'Selecciona un producto antes de filtrar'
          }
        },
        products: {
          selectAllProducts: '< SELECCIONAR PRODUCTOS >'
        },
        supportHelpCenter: {
          title: 'Centro Ayuda'
        },
        spectraEquipments: {
          title: 'Equipos Nir',
          searchPlaceholder: 'Buscar equipo',
          add: 'Añadir equipo',
          edit: 'Editar',
          delete: 'Eliminar',
          empty: 'Ningún equipo coincide con la búsqueda.',
          none: 'No hay equipos para esta empresa.',
          loadError: 'No se pudieron cargar los equipos.',
          columns: {
            name: 'Equipo',
            companyGroup: 'Grupo de empresa',
            location: 'Localización',
            manufacturer: 'Fabricante',
            model: 'Modelo',
            serialNumber: 'Nº serie',
            resolution: 'Resolución Wavelength',
            notes: 'Notas'
          },
          form: {
            addTitle: 'Añadir equipo',
            editTitle: 'Editar equipo',
            close: 'Cerrar',
            cancel: 'Cancelar',
            save: 'Guardar equipo',
            saveChanges: 'Guardar cambios',
            optional: 'Opcional',
            required: 'Este campo es obligatorio',
            labels: {
              name: 'Nombre / identificador',
              companyGroup: 'Grupo de empresa',
              location: 'Localización',
              manufacturer: 'Fabricante',
              model: 'Modelo',
              serialNumber: 'Número de serie',
              resolution: 'Resolución Wavelength',
              notes: 'Notas'
            }
          }
        },
        spectraImport: {
          title: 'Subir espectros',
          continue: 'Continuar',
          back: 'Atrás',
          steps: {
            equipment: 'Equipo',
            file: 'Archivo',
            validation: 'Validación',
            result: 'Resultado'
          },
          equipment: {
            instruction: 'Selecciona el equipo NIR al que pertenecen estos espectros.',
            searchPlaceholder: 'Buscar por nombre, fabricante o modelo',
            manage: '¿No ves tu equipo? Gestionar equipos',
            empty: 'Ningún equipo coincide con la búsqueda.',
            none: 'No hay equipos para esta empresa.',
            loadError: 'No se pudieron cargar los equipos.'
          },
          file: {
            uploadingFor: 'Subiendo espectros para el equipo',
            dropTitle: 'Arrastra el archivo aquí o haz clic para buscarlo',
            supportedFormats: 'Formatos admitidos: .csv, .nir, .sp, .spc, .jdx, .json, .opus, .spa',
            removeFile: 'Quitar archivo',
            invalidFormat: 'Formato no admitido. Usa csv, .nir, .sp, .spc, .jdx, .json, .opus, .spa.',
            downloadTemplate: 'Descargar plantilla CSV',
            howToFill: 'Cómo rellenar el CSV',
            howToFillIntro: 'Cada fila es una lectura de espectro. Columnas obligatorias:',
            howToFillItems: {
              sampleId: 'ID muestra — único por fila',
              product: 'Código producto / Nombre producto — debe existir en el catálogo',
              batch: 'Número de lote, Fecha de registro (DD/MM/YYYY), Repetición (≥1)',
              spectrum: 'Longitud de onda / Absorbancia — un par de columnas por punto medido, dentro del rango de calibración del equipo'
            },
            validate: 'Validar archivo'
          },
          validation: {
            fileReadOk: 'Archivo leído correctamente',
            reading: 'Leyendo el archivo y comprobando espectros…',
            readingHint: 'Suele tardar unos segundos.',
            found: '{{count}} espectros encontrados',
            valid: '{{count}} válidos',
            withErrors: '{{count}} con errores',
            breakdown: '({{blocking}} bloqueantes, {{warnings}} avisos).',
            blockingBanner: 'Hay {{count}} errores. Vuelve atrás y sube un archivo corregido para continuar.',
            loadError: 'No se pudo validar el archivo.',
            importAction: 'Importar {{count}} espectros',
            columns: {
              row: 'Fila',
              sample: 'Muestra',
              field: 'Campo',
              error: 'Error',
              recommendedAction: 'Acción recomendada',
              status: 'Estado'
            },
            statusBlocking: 'Bloqueante',
            statusWarning: 'Aviso'
          },
          result: {
            title: 'Importación completada',
            summary: 'Se importaron {{count}} espectros y se asociaron a',
            viewImported: 'Ver espectros importados',
            importAnother: 'Importar otro archivo'
          }
        },
        spectraViewer: {
          title: 'Visor Espectros',
          actions: {
            hideFilter: 'Ocultar filtro',
            showFilter: 'Mostrar filtro'
          },
          chart: {
            zoom: 'Zoom',
            resetZoom: 'Reiniciar zoom',
            saveImage: 'Guardar imagen',
            enableInteractive: 'Activar cruz y tooltip',
            disableInteractive: 'Desactivar cruz y tooltip',
            showLegend: 'Mostrar leyenda',
            hideLegend: 'Ocultar leyenda',
            showChartTitle: 'Mostrar título del gráfico',
            hideChartTitle: 'Ocultar título del gráfico',
            productNamesEmpty: 'Sin nombre de producto',
            legendLabelsByLot: 'Leyenda: número de lote',
            legendLabelsByProduct: 'Leyenda: nombre de producto',
            toggleLegendLabelsToProduct: 'Leyenda por nombre de producto',
            toggleLegendLabelsToLot: 'Leyenda por número de lote'
          },
          dialogs: {
            selectProductBeforeFilter: 'Selecciona un producto antes de filtrar',
            wavelengthsNotSame: 'Los espectros seleccionados no tienen las mismas longitudes de onda.',
            tooManyForChart: 'Demasiados espectros para mostrar en la gráfica ({{count}}). El máximo permitido es {{max}}.',
            saveBatchFailed: 'No se pudo guardar el lote. {{error}}',
            saveAdditionalIdentifierFailed: 'No se pudo guardar el identificador adicional. {{error}}',
            saveProductFailed: 'No se pudo guardar el producto. {{error}}',
            saveSupplierFailed: 'No se pudo guardar el proveedor. {{error}}',
            confirmDelete: '¿Seguro que quieres eliminar {{count}} espectro(s) seleccionado(s)? Esta acción no se puede deshacer.',
            deleting: 'Eliminando {{count}} espectro(s)…',
            deletedOk: 'Eliminados {{count}} espectro(s) correctamente.',
            deleteNoneDeleted: 'No se pudo eliminar ninguno de los {{count}} espectro(s).',
            deleteSomeFailed: 'Eliminados {{ok}} espectro(s). No se pudieron eliminar {{failed}}.',
            notDeletedLine: 'Número Lote: {{batch}}  -  F. Registro: {{reg}}  -  Repetición: {{rep}}'
          }
        }
      },
      menu: {
        groups: {
          spectra: 'Espectros',
          analytics: 'Analítica',
          support: 'Soporte'
        },
        items: {
          spectraViewer: 'Visor Espectros',
          spectraImport: 'Importar Espectros',
          equipments: 'Equipos Nir',
          results: 'Resultados',
          dashboards: 'Dashboards',
          helpCenter: 'Centro Ayuda'
        }
      },
      datagrid: {
        headers: {
          productCode: 'Codigo Producto',
          productName: 'Nombre Producto',
          batchNumber: 'Numero Lote',
          registrationDate: 'Fecha Registro',
          repetition: 'Repeticion',
          additionalIdentifier: 'Identificador Adicional',
          supplierName: 'Proveedor',
          conformity: 'Conformidad'
        }
      },
      supportTicket: {
        tooltip: 'Soporte',
        title: 'Ticket de soporte',
        intro: 'Describe el problema para que podamos comprender la situación y ayudarte a resolverlo. Puedes adjuntar archivos para ayudarnos a entenderlo mejor.',
        placeholder: 'Describe tu problema o solicitud…',
        subject: 'Ticket de soporte',
        success: 'Ticket enviado correctamente.',
        email: {
          greeting: 'Estimado/a {{name}},',
          received: 'Hemos recibido tu ticket y se ha creado correctamente.',
          reviewing: 'El equipo de soporte de Syn está revisando tu solicitud para mejorar y resolver los problemas lo antes posible.',
          userInformationTitle: 'Información del usuario:',
          ticketDescriptionTitle: 'Descripción del ticket:',
          fields: {
            name: 'Nombre',
            email: 'Email',
            company: 'Empresa'
          }
        },
        actions: {
          send: 'Enviar ticket',
          sending: 'Enviando…'
        },
        attach: {
          tooltip: 'Adjuntar ficheros',
          aria: 'adjuntar ficheros'
        },
        aria: {
          open: 'abrir ticket de soporte',
          close: 'cerrar',
          removeAttachment: 'quitar {{fileName}}'
        },
        errors: {
          sendFailed: 'No se pudo enviar el ticket.',
          sendFailedWithStatus: 'Error enviando ticket: {{status}} {{statusText}}',
          attachmentsTotalTooLarge: 'El tamaño total de los adjuntos no puede superar {{maxMB}} MB.',
          attachmentsTotalTooLargeSomeOmitted: 'Se han omitido algunos ficheros: el tamaño total de los adjuntos no puede superar {{maxMB}} MB.'
        }
      },
      auth: {
        actions: {
          logout: 'Cerrar sesión'
        },
        session: {
          warningTitle: 'La sesión va a caducar',
          warningMessage: 'Por seguridad, cerraremos tu sesión en {{time}}.\nInicia sesión de nuevo para continuar trabajando.',
          logoutNow: 'Cerrar sesión ahora'
        },
        login: {
          title: 'Bienvenido a Syn',
          titlePrefix: 'Bienvenido a',
          titleSuffix: 'Syn',
          fields: {
            email: 'Correo electrónico',
            password: 'Contraseña'
          },
          placeholders: {
            email: 'Introduce tu correo',
            password: 'Introduce tu contraseña'
          },
          rememberCredentials: 'Recordar credenciales',
          forgotPassword: '¿Olvidaste la contraseña?',
          actions: {
            login: 'Iniciar sesión',
            loggingIn: 'Iniciando sesión ...'
          },
          validation: {
            emailInvalid: 'Debe ser un correo válido',
            emailRequired: 'El correo es obligatorio',
            passwordRequired: 'La contraseña es obligatoria',
            passwordNoEdgeSpaces: 'La contraseña no puede empezar o terminar con espacios',
            passwordMax: 'La contraseña debe tener menos de 20 caracteres'
          },
          errors: {
            loginFailed: 'Error en el login',
            connectionFailed: 'Error de conexión. Inténtalo de nuevo.'
          }
        },
        forgotPassword: {
          title: 'Recuperar Contraseña',
          intro: 'Introduce tu correo y te enviaremos instrucciones para restablecer tu contraseña. Revisa tu bandeja de entrada.',
          fields: {
            email: 'Correo electrónico'
          },
          placeholders: {
            email: 'Introduce tu correo'
          },
          actions: {
            send: 'Enviar email',
            sending: 'Enviando ...',
            backToLogin: 'Volver al login'
          },
          validation: {
            emailInvalid: 'Debe ser un correo válido',
            emailRequired: 'El correo es obligatorio'
          },
          errors: {
            sendFailed: 'No se pudo enviar el email'
          }
        },
        resetPassword: {
          title: 'Cambiar contraseña',
          intro: 'Introduce tu nueva contraseña y confírmala',
          fields: {
            newPassword: 'Nueva contraseña',
            confirmPassword: 'Confirmar contraseña'
          },
          placeholders: {
            newPassword: 'Introduce la nueva contraseña',
            confirmPassword: 'Confirma la nueva contraseña'
          },
          actions: {
            save: 'Guardar nueva contraseña',
            saving: 'Guardando ...'
          },
          validation: {
            passwordRequired: 'La contraseña es obligatoria',
            passwordMin: 'Mínimo 8 caracteres',
            passwordMax: 'Máximo 64 caracteres',
            passwordLettersAndNumbers: 'Debe contener letras y números',
            passwordNoEdgeSpaces: 'La contraseña no puede empezar o terminar con espacios',
            confirmPasswordRequired: 'Confirma tu contraseña',
            passwordsMustMatch: 'Las contraseñas deben coincidir'
          },
          errors: {
            resetFailed: 'El enlace ha expirado y la contraseña no se pudo restablecer. Por favor, solicite un nuevo enlace.',
            resetFailedWithStatus: 'No se pudo restablecer la contraseña: {{status}} {{statusText}}',
            missingToken: 'Falta el token. Solicita un nuevo email de restablecimiento.',
            invalidToken: 'Token inválido o malformado.'
          },
          success: {
            resetOk: 'La contraseña se cambio correctamente. Inicia sesión.'
          }
        }
      }
    }
  }
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: savedLng,
    fallbackLng: 'en',
    interpolation: { escapeValue: false },
    ns: ['common'],
    defaultNS: 'common'
  });

export default i18n;


